import React, { useRef, useState } from 'react';
import { Button, Spinner, TextInput } from 'flowbite-react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import CustomModal from '../Modal/CustomModal';
import CreatePinModal from '../CreatePinModal/CreatePinModal';
import AnswerSecurityQuestions from '../AnswerSecurityQuestions/AnswerSecurityQuestions';

interface OtpVerificationModalProps {
    close: () => void;
    type?: string;
    closeAllModals: () => void;
}

const OtpVerificationModal = ({ close, type, closeAllModals }: OtpVerificationModalProps) => {
    const OTP_LENGTH = 6;
    const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [openCreatePinModal, setOpenCreatePinModal] = useState<boolean>(false);
    const [openSecretQuestionUi, setOpenSecretQuestionUi] = useState<boolean>(false);
    const [showOtp, setShowOtp] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = e.target;
        if (value.length > 1 || isNaN(Number(value))) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < OTP_LENGTH - 1) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace') {
            if (!otp[index] && index > 0) {
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
                otpRefs.current[index - 1]?.focus();
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text').trim().slice(0, OTP_LENGTH);
        if (!/^\d+$/.test(pasteData)) return;

        const newOtp = pasteData.split('');
        setOtp(newOtp.concat(new Array(OTP_LENGTH - newOtp.length).fill('')));

        // Focus on the first empty input
        const firstEmptyIndex = newOtp.length;
        if (firstEmptyIndex < OTP_LENGTH) {
            otpRefs.current[firstEmptyIndex]?.focus();
        }
    };

    const handleSubmit = () => {
        if (otp.includes('')) {
            setError('Please enter the full OTP.');
            return;
        }
        setError('');
        if (type === 'reset') {
            setOpenSecretQuestionUi(true);
        } else {
            setOpenCreatePinModal(true);
        }
    };

    return (
        <div className="p-6 w-full max-w-sm mx-auto bg-white rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold text-center mb-2">OTP Verification</h2>
            <p className="text-sm text-gray-500 mb-4 text-center">
            Please check your email for One-Time Password (OTP) to proceed
            </p>

            {/* OTP Inputs */}
            <div className="flex justify-center items-center gap-3 mb-4">
                {otp.map((digit, index) => (
                    <TextInput
                        key={index}
                        type={showOtp ? 'text' : 'password'}
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        ref={(el) => (otpRefs.current[index] = el)}
                        className="w-12 text-center text-xl font-bold border border-gray-300 rounded-lg focus:border-blue-500"
                        aria-label={`OTP digit ${index + 1}`}
                    />
                ))}
            </div>

            {/* Toggle Visibility */}
            <div className="flex justify-center mb-3">
                <button type="button" onClick={() => setShowOtp(!showOtp)}>
                    {showOtp ? <FaEyeSlash className="w-5 h-5 text-gray-600" /> : <FaEye className="w-5 h-5 text-gray-600" />}
                </button>
            </div>

            {/* Error Message */}
            {error && <p className="text-red-500 text-sm text-center mb-3">{error}</p>}

            {/* Resend OTP */}
            <p className="text-sm text-green-600 text-center cursor-pointer hover:underline mb-4">
                Resend OTP
            </p>

            {/* Buttons */}
            <div className="flex justify-center gap-4">
                <Button
                    onClick={close}
                    className="bg-white text-black border border-gray-300 hover:bg-gray-100 px-4 py-2 rounded-md"
                >
                    Close
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={otp.includes('') || loading}
                    className={`px-4 py-2 rounded-md transition ${
                        otp.includes('') || loading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-custom-button-green text-white hover:bg-green-600'
                    }`}
                >
                    {loading ? <Spinner size="sm" /> : 'Next'}
                </Button>
            </div>

            {/* Modals */}
            <CustomModal dismissible size="md" onClose={() => setOpenCreatePinModal(false)} icon="" show={openCreatePinModal}>
                <CreatePinModal closeAllModals={closeAllModals} otp={otp.join('')} close={() => setOpenCreatePinModal(false)} message="Create a 4-digit PIN" buttonText="Next" onSuccess={() => {}} type="create" />
            </CustomModal>
            <CustomModal dismissible size="md" onClose={() => setOpenSecretQuestionUi(false)} icon="" show={openSecretQuestionUi}>
                <AnswerSecurityQuestions otp={otp.join('')} closeAllModals={close} />
            </CustomModal>
        </div>
    );
};

export default OtpVerificationModal;
